/**
 * Cross-tab broadcast channel built on top of the `localStorage` `storage` event.
 *
 * Consolidates three patterns that previously lived inline in
 * `useSessionTimeout`, `api.ts` and `logout.ts`:
 *   - SSR guard (`globalThis.window` is undefined during Next.js server render)
 *   - try/catch around `localStorage` operations that can throw in private
 *     mode or under quota pressure
 *   - one shared `storage` listener that fans out to per-channel subscribers,
 *     so each consumer doesn't register its own document-wide listener
 *
 * Channel names get the `gail:` prefix automatically; pass the unprefixed
 * short name (e.g. "lastActivityAt"). The `storage` event only fires in
 * *other* tabs, which is exactly the semantic cross-tab callers want.
 */

type CrossTabHandler = (value: string) => void;

const KEY_PREFIX = "gail:";

const subscribers = new Map<string, Set<CrossTabHandler>>();
let listenerInstalled = false;

function handleStorage(event: StorageEvent): void {
  // `newValue` is null only for removeItem/clear; an empty string is a valid
  // setItem update and should be delivered.
  if (!event.key || event.newValue === null) return;
  const handlers = subscribers.get(event.key);
  if (!handlers) return;
  for (const handler of handlers) {
    handler(event.newValue);
  }
}

function ensureListener(): void {
  if (listenerInstalled) return;
  const win = globalThis.window;
  // Defensive about both SSR (window undefined) and tests that stub window
  // with a partial object lacking addEventListener.
  if (!win || typeof win.addEventListener !== "function") return;
  win.addEventListener("storage", handleStorage);
  listenerInstalled = true;
}

/**
 * Subscribe to a channel. Returns an unsubscribe function. The underlying
 * `storage` listener is installed lazily on first subscription.
 */
export function subscribe(channel: string, handler: CrossTabHandler): () => void {
  const key = `${KEY_PREFIX}${channel}`;
  ensureListener();
  let set = subscribers.get(key);
  if (!set) {
    set = new Set();
    subscribers.set(key, set);
  }
  set.add(handler);
  return () => {
    const current = subscribers.get(key);
    if (!current) return;
    current.delete(handler);
    if (current.size === 0) subscribers.delete(key);
  };
}

/**
 * Write `value` so other tabs receive a `storage` event. Returns true on
 * success. On failure (private mode, quota, disabled storage) the supplied
 * `onError` callback fires and the function returns false — callers decide
 * whether to log, retry, or silently degrade.
 */
export function broadcast(
  channel: string,
  value: string,
  onError?: (error: unknown) => void,
): boolean {
  if (typeof globalThis.window === "undefined") return false;
  const storage = globalThis.localStorage;
  if (!storage) {
    onError?.(new Error("localStorage unavailable"));
    return false;
  }
  try {
    storage.setItem(`${KEY_PREFIX}${channel}`, value);
    return true;
  } catch (error) {
    onError?.(error);
    return false;
  }
}

/**
 * Read the current value for a channel without subscribing. Returns null
 * when the channel has no value or storage is unavailable.
 */
export function readLatest(channel: string): string | null {
  if (typeof globalThis.window === "undefined") return null;
  try {
    return globalThis.localStorage?.getItem(`${KEY_PREFIX}${channel}`) ?? null;
  } catch {
    return null;
  }
}

/** @internal Test-only — clear subscribers and detach the shared listener. */
export function _resetCrossTabChannel(): void {
  subscribers.clear();
  const win = globalThis.window;
  if (listenerInstalled && win && typeof win.removeEventListener === "function") {
    win.removeEventListener("storage", handleStorage);
  }
  listenerInstalled = false;
}
